import { useEffect, useState, useCallback } from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const TAGMANGO_URL = process.env.REACT_APP_PURCHASE_URL || "https://learn.dhokiyas.com/l/d04841432c";

let cachedGeo = null;

/**
 * Geo-routed checkout (DIRECT REDIRECT — no popup form).
 *
 *   India  → Cashfree hosted checkout. We create the order server-side with
 *            an anonymous customer placeholder; Cashfree collects email/name/
 *            phone on their own checkout page (so we don't ask twice).
 *   Else   → TagMango hosted checkout (existing redirect link).
 */
export function useGeoCheckout() {
  const [geo, setGeo] = useState(cachedGeo);

  useEffect(() => {
    if (cachedGeo) { setGeo(cachedGeo); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/payments/geo`, { cache: "no-store" });
        if (!r.ok) throw new Error("geo");
        const data = await r.json();
        cachedGeo = data;
        if (!cancelled) setGeo(data);
      } catch {
        const fallback = { country: "UN", is_india: false, currency: "INR", amount: 4999, gateway: "cashfree", tagmango_url: TAGMANGO_URL };
        cachedGeo = fallback;
        if (!cancelled) setGeo(fallback);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const checkout = useCallback(async ({ email, name, phone } = {}) => {
    if (!geo) return;
    if (!geo.is_india) {
      // Global → TagMango
      window.open(geo.tagmango_url || TAGMANGO_URL, "_blank", "noopener");
      return;
    }
    // India → Cashfree direct redirect (no upfront form)
    try {
      const r = await fetch(`${API_BASE}/api/payments/cashfree/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email || "",
          name: name || "",
          phone: phone || "",
        }),
      });
      if (!r.ok) throw new Error("create-order failed");
      const order = await r.json();
      const sessionId = order.payment_session_id;
      if (!sessionId) throw new Error("no payment_session_id");
      // Redirect → Cashfree's hosted checkout. They collect email/name/phone there.
      
      if (!window.Cashfree) {
        await new Promise((resolve, reject) => {
          const existing = document.querySelector('script[src="https://sdk.cashfree.com/js/v3/cashfree.js"]');
          if (existing) {
            existing.onload = resolve;
            existing.onerror = reject;
            return;
          }
          const script = document.createElement("script");
          script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      const cashfree = window.Cashfree({ mode: "production" });
      await cashfree.checkout({
        paymentSessionId: sessionId,
        redirectTarget: "_self"
      });

    } catch (e) {
      console.error("Cashfree redirect failed:", e);
      // Fallback so the user is never stuck
      window.open(TAGMANGO_URL, "_blank", "noopener");
    }
  }, [geo]);

  return {
    geo,
    isIndia: !!geo?.is_india,
    currency: geo?.currency || "USD",
    amount: geo?.amount || 50,
    gateway: geo?.gateway || "tagmango",
    checkout,
  };
}
